import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect } from 'react-redux'
import { List, Map, fromJS } from 'immutable'
import _get from 'lodash/get'
import _omit from 'lodash/omit'
import { useLazyQuery, useMutation } from '@apollo/client'
import { getBoundActions } from '../../actions/index'
import { getPrimary } from '../../services/languages/index'
import {
  GET_PLAYLISTS,
  DELETE_PLAYLIST,
  GET_PLAYLIST_ITEMS,
  REMOVE_FROM_PLAYLIST,
} from '../../services/playlist/query'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../services/apollo-graphql/index'
import {
  CONTENT_IMAGE_LIST,
  getFormattedContentData,
} from '../../services/content/index'
import {
  TYPE_USER_PLAYLIST_DELETE,
  TYPE_USER_PLAYLIST_RENAME,
  TYPE_USER_PLAYLIST_SHARE,
} from '../../services/dialog/index'
import { PLAY_LIST_SHARED_GA4_EVENT } from '../../services/event-tracking/index'
import RowV3 from '../Row.v3/index'
import { STYLES } from '../Row.v3/utils'
import TileV2Graphql from '../Tile.v2/Tile.v2Graphql/index'
import LoadMore from '../LoadMore/index'
import Button, { TYPES } from '../Button/index'
import IconV2, { ICON_TYPES, ICON_STYLES } from '../Icon.v2/index'
import { H2, HEADING_TYPES } from '../Heading/index'
import {
  PLAYLIST_FETCH_LIMIT,
  getPlaceholderList,
} from './utils'
import MyLibraryPlaylistHeader from './MyLibraryPlaylistHeader'

const MyLibraryPlaylist = (props) => {
  const {
    pageState = {},
    auth,
    user,
    addToasty,
    staticText,
    renderModal,
    dismissModal,
    onPlaylistRename,
    setEventDefault,
  } = props

  const authJwt = auth.get('jwt')
  const placeholderList = getPlaceholderList()
  const language = getPrimary(user.getIn(['data', 'language'], List()))

  const [playlistId, setPlaylistId] = useState()
  const [playlistType, setPlaylistType] = useState()
  const [itemsList, setItemsList] = useState(List())
  const [totalItemsCount, setTotalItemsCount] = useState(0)
  const [hasItems, setHasItems] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [selectedItems, setSelectedItems] = useState({})

  const [
    getPlaylistItemList,
    {
      data,
      loading,
      fetchMore,
      refetch,
    },
  ] = useLazyQuery(GET_PLAYLIST_ITEMS, {
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
  })

  const [
    removeFromPlaylist,
    { error: removeFromPlaylistError, loading: removingFromPlaylist },
  ] = useMutation(REMOVE_FROM_PLAYLIST, {
    context: {
      jwt: authJwt,
    },
  })

  const [
    deletePlaylist,
    { error: deletePlaylistError, loading: deletingPlaylist },
  ] = useMutation(DELETE_PLAYLIST, {
    refetchQueries: [
      GET_PLAYLISTS,
      'playlistList',
    ],
    context: {
      jwt: authJwt,
    },
  })

  const items = itemsList.size && !loading ? itemsList : placeholderList

  const handleEditSubmit = async () => {
    const itemsToRemove = Object.values(selectedItems)
    if (itemsToRemove.length && !removingFromPlaylist) {
      const promises = itemsToRemove.map((item) => {
        return removeFromPlaylist({
          variables: {
            playlistId,
            playlistType,
            contentId: item.get('contentId'),
            contentType: item.get('contentType'),
          },
        })
      })
      await Promise.all(promises)
      if (removeFromPlaylistError) {
        addToasty(staticText.get('savePlaylistError'))
      } else {
        addToasty(staticText.get('savePlaylistSuccess'))
      }
      setSelectedItems({})
      refetch()
    }
  }

  const handleEditClick = async () => {
    if (!editMode) {
      setEditMode(true)
    } else {
      setEditMode(false)
      handleEditSubmit()
    }
  }

  const handleDeleteSubmit = async (type) => {
    if (!deletingPlaylist) {
      await deletePlaylist({
        variables: {
          playlistType: type,
        },
      })
      if (deletePlaylistError) {
        addToasty(staticText.get('deletePlaylistError'))
      } else {
        addToasty(staticText.get('deletePlaylistSuccess'))
        dismissModal()
      }
    }
  }

  const handleDeleteClick = () => {
    renderModal(TYPE_USER_PLAYLIST_DELETE, {
      playlistType,
      playlistName: pageState.name,
      playlistSize: itemsList.size || 0,
      onDeleteSubmit: (type) => handleDeleteSubmit(type),
      onReturnFocus: () => {},
    })
  }

  const handleRenameClick = () => {
    renderModal(TYPE_USER_PLAYLIST_RENAME, {
      playlistType,
      playlistName: pageState.name,
      onRenameSuccess: onPlaylistRename,
      onReturnFocus: () => {},
    })
  }

  const trackShareEvent = () => {
    // GA4 event
    setEventDefault(PLAY_LIST_SHARED_GA4_EVENT.mergeDeep({
      playlist_name: pageState.name,
      playlist_type: 'Custom',
      ui_location: 'My Library',
    }))
  }

  const handleShareClick = () => {
    trackShareEvent()
    renderModal(TYPE_USER_PLAYLIST_SHARE, {
      playlistName: pageState.name,
      playlistType,
      playListItemsGraphql: itemsList.map((item) => item.get('contentId')),
    })
  }

  const toggleItemAddRemove = useCallback((item, isSelected) => {
    if (isSelected) {
      setSelectedItems((prevState) => _omit(prevState, [item.get('id')]))
    } else {
      setSelectedItems((prevState) => ({ ...prevState, [item.get('id')]: item }))
    }
  }, [setSelectedItems])

  const renderItem = useCallback((item, index) => {
    const tileData = getFormattedContentData(item.getIn(['content', 0]))
    const isSelected = !!selectedItems[item.get('id')]

    if (editMode) {
      return (
        <div className="my-library__item-edit" key={tileData.get('id') || index}>
          <div className={`my-library__item-edit-backdrop ${isSelected ? 'my-library__item-edit-backdrop--add' : ''}`}></div>
          <Button
            type={TYPES.LINK}
            onClick={() => toggleItemAddRemove(item, isSelected)}
            className={`my-library__item-edit-button my-library__item-edit-button--${isSelected ? 'add' : 'remove'}`}
          >
            <IconV2
              className="my-library__item-edit-icon"
              type={isSelected ? ICON_TYPES.PLUS : ICON_TYPES.CLOSE}
            />
          </Button>
          <TileV2Graphql
            data={tileData}
            adminTitle="My Library"
            index={index}
            uiLocation="My Library"
          />
        </div>
      )
    }

    return (
      <TileV2Graphql
        key={tileData.get('id') || index}
        data={tileData}
        adminTitle="My Library"
        index={index}
        uiLocation="My Library"
      />
    )
  }, [editMode, selectedItems, toggleItemAddRemove])

  useEffect(() => {
    if (data) {
      const playlistItemList = _get(data, ['playlistItemList'], {}) || {}
      const playlistItems = fromJS(_get(data, ['playlistItemList', 'playlistItems'], []) || [])
      const totalCount = _get(playlistItemList, ['count'], 0)
      setItemsList(playlistItems)
      setTotalItemsCount(totalCount)
      setHasItems(totalCount > 0)
    }
  }, [data])

  useEffect(() => {
    if (playlistId && playlistType) {
      getPlaylistItemList({
        variables: {
          language: [language, '_primary'],
          imageType: CONTENT_IMAGE_LIST,
          limit: PLAYLIST_FETCH_LIMIT,
          order: [['playlistPosition', 'DESC']],
          learnerLevelLanguage: language,
          playlistId,
          playlistType,
          offset: 0,
        },
        context: {
          jwt: authJwt,
        },
      })
    } else {
      setTotalItemsCount(0)
      setHasItems(false)
    }
  }, [playlistId, playlistType, language, authJwt, getPlaylistItemList])

  useEffect(() => {
    if (pageState && pageState.id !== playlistId) {
      setPlaylistId(pageState.id)
      setPlaylistType(pageState.type)
      setHasItems(true)
      setEditMode(false)
    }
  }, [pageState])

  return (
    <div className="my-library__content">
      <MyLibraryPlaylistHeader
        pageState={pageState}
        itemsList={itemsList}
        staticText={staticText}
        editMode={editMode}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        onRenameClick={handleRenameClick}
        onShareClick={handleShareClick}
      />
      {hasItems && playlistId
        ? <>
          <RowV3
            asGrid
            style={STYLES.S12334}
          >
            {items.map((item, index) => renderItem(item, index))}
          </RowV3>
          <LoadMore
            onLoadMore={() => fetchMore({
              variables: {
                offset: itemsList.size,
              },
            })}
            buttonClassName={['my-library__load-more-button', 'button--secondary']}
            currentCount={itemsList.size}
            totalCount={totalItemsCount}
            processing={loading || !itemsList.size}
            infiniteScroll
          />
        </>
        : <div className="my-library__content-empty">
          <div className="my-library__content-empty-image" />
          <div className="my-library__content-empty-text">
            <H2 as={HEADING_TYPES.H5}>{staticText.get('playlistEmptyTitle')}</H2>
            <p className="my-library__content-empty-copy">
              {staticText.get('playlistEmptyText1')}
              <IconV2 type={ICON_TYPES.PLUS} style={ICON_STYLES.OUTLINE} />
              {staticText.get('playlistEmptyText2')}
            </p>
          </div>
        </div>
      }
    </div>
  )
}

MyLibraryPlaylist.propTypes = {
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  pageState: PropTypes.object.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  onPlaylistRename: PropTypes.func,
}

export default connect(
  (state) => {
    return {
      auth: state.auth,
      user: state.user,
      staticText: state.staticText.getIn(['data', 'myLibraryPage', 'data'], Map()) || Map(),
    }
  },
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      addToasty: actions.toasty.addToasty,
      renderModal: actions.dialog.renderModal,
      dismissModal: actions.dialog.dismissModal,
      setEventDefault: actions.eventTracking.setEventDefault,
    }
  },
)(MyLibraryPlaylist)
